import { EntityLink } from 'rfa-react-core';
import PropTypes from 'prop-types';
import React from 'react';
import StoryCard from 'app/components/partials/story-card';
import { truncateOnWordBoundary } from 'app/utilities/truncate';
import { metaTime, publishedTime } from 'app/utilities/date-time';

const CHARACTER_LIMIT = {
    DESCRIPTION: 140,
    TITLE: 90
};

// Which colour each featured story is on, in the order the CMS lists them
const TONES = ['is-primary', 'is-secondary'];

/**
 * One featured story: its image above, and its own colour behind the text.
 *
 * @param {object} item Story, in the `{ id, type, data }` form tiles expect
 * @param {number} index Place in the feature list, which decides the colour
 *
 * @return {ReactElement} the story
 */
const renderStory = (item, index) => {
    const {
        author,
        name,
        publish_at: publishDate,
        short_description: description,
        thumbnail
    } = item.data;

    return (
        <article key={item.id} className={`category-feature-story ${TONES[index] || ''}`}>
            <EntityLink
                item={item}
                promoterBuName="Auckland Zoo"
                className="category-feature-story-link"
            >
                {/*
                    The largest images on the page and the ones most worth having
                    in image search, so they are elements with the headline as
                    their description rather than backgrounds. The upper story is
                    what the reader lands on, so it loads at the normal priority.
                */}
                {thumbnail ?
                    <img
                        className="category-feature-story-image"
                        src={thumbnail}
                        alt={name}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                    /> :
                    <div className="category-feature-story-image is-placeholder" />
                }
                <div className="category-feature-story-content">
                    <h2 className="title">{truncateOnWordBoundary(name, CHARACTER_LIMIT.TITLE)}</h2>
                    {description &&
                        <p className="subtitle">
                            {truncateOnWordBoundary(description, CHARACTER_LIMIT.DESCRIPTION)}
                        </p>
                    }
                    <p className="meta">
                        {author && <span className="author">by {author}</span>}
                        {publishDate && <br />}
                        {publishDate && <time dateTime={metaTime(publishDate)}>{publishedTime(publishDate)}</time>}
                    </p>
                </div>
            </EntityLink>
        </article>
    );
};

/*
    The row a level 2 category opens with: the stories its CMS entry features
    stacked down two thirds of the width, and the first few of the listing
    itself filling the third they leave.

    The two feature tiles are both shown rather than taking turns in a carousel,
    so every headline in the row is in the markup as it stands. `tile-grid` is
    what splits the row into columns, shared with the landing page hero so the
    two rows are the same shape rather than two sets of numbers to keep in step.
    There is no `constrain-width` here: the row is placed inside the one its
    section already opens.
*/
const CategoryFeature = ({ featuredNews = [], stories = [] }) => {
    if (!featuredNews.length) return null;

    return (
        <div className="category-feature">
            <div className="tile-grid">
                <div className="category-feature-main">
                    {featuredNews.map(renderStory)}
                </div>
                <div className="tile-grid-sidebar">
                    <div className="tile-grid-sidebar-inner">
                        <div className="category-feature-list">
                            {stories.map((item) => <StoryCard key={item.id} item={item} />)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

CategoryFeature.propTypes = {
    featuredNews: PropTypes.array,
    stories: PropTypes.array
};

export default CategoryFeature;
